/**
 * Global fix för Radix UI-buggen som orsakar att sidan "fryser" (blir oklickbar)
 * efter att en Dialog stängts samtidigt som en toast/annan modal öppnas.
 *
 * Bakgrund: Radix sätter `pointer-events: none` på <body> när en Dialog är öppen,
 * och tar bort det när dialogen stängs. Om två stateändringar sker samtidigt
 * (t.ex. dialog stängs + toast öppnas) kan cleanup missas och body:n förblir
 * blockerad — sidan ser normal ut men inga klick fungerar. Användaren tvingas
 * ladda om sidan.
 *
 * Referens: https://github.com/radix-ui/primitives/issues/2122
 *
 * Denna vakt:
 *   1. Lyssnar på interaktioner (pointer/click/focus) på document (capture-fas)
 *   2. Om <body> är låst men INGEN öppen dialog finns → rensa låset direkt
 *   3. Kör även en MutationObserver som fångar när sista dialogen försvinner
 */
export function installBodyPointerEventsGuard() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__operioPointerGuardInstalled) return;
  window.__operioPointerGuardInstalled = true;

  const hasOpenOverlay = () => {
    // Radix Dialog / Sheet / AlertDialog / Popover när de är öppna
    return !!document.querySelector(
      '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], [data-radix-focus-guard]'
    );
  };

  const clearIfStuck = () => {
    const body = document.body;
    if (!body) return;
    const stuck = body.style.pointerEvents === 'none' || body.hasAttribute('data-scroll-locked');
    if (stuck && !hasOpenOverlay()) {
      body.style.pointerEvents = '';
      body.removeAttribute('data-scroll-locked');
      // Radix låser även html-elementet i vissa fall
      const html = document.documentElement;
      if (html?.style?.pointerEvents === 'none') html.style.pointerEvents = '';
    }
  };

  // Kör vakten vid alla användarinteraktioner (capture så vi vinner)
  const handlers = ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'focus'];
  handlers.forEach((ev) =>
    document.addEventListener(ev, clearIfStuck, { capture: true, passive: true })
  );

  // Fånga också DOM-ändringar (dialog demonteras) — reagerar direkt utan att vänta på klick
  try {
    const mo = new MutationObserver(() => clearIfStuck());
    mo.observe(document.body, { attributes: true, childList: true, subtree: true, attributeFilter: ['data-state', 'style'] });
  } catch (_) { /* MutationObserver saknas — ignorera */ }

  // Säkerhetsnät: kontrollera varje sekund
  setInterval(clearIfStuck, 1000);
}
